import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Typography, Button, Modal, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Chip, Autocomplete, Grid, Divider, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, InputAdornment } from '@mui/material';
import { PermissionService } from '../pages/Permissions/service';
import { StaffService } from '../api/services/staffService';

const normalizeRole = (role) => {
    const roleName = typeof role === 'object' && role !== null ? role.name : role;
    return String(roleName || '').replace(/[_\s-]+/g, '').toUpperCase();
};

const isStaffBasedRoleName = (roleName) => {
    if (!roleName) return false;
    const normalized = String(roleName).replace(/[_\s-]+/g, '').toUpperCase();
    const nonStaffRoles = [
        'SUPERADMIN',
        'ADMIN',
        'HOTELADMIN',
        'APIUSER',
        'AUDITOR',
        'EXTERNALVENDOR',
        'CORPORATEADMIN'
    ];
    return !nonStaffRoles.includes(normalized);
};

const getHotelId = (user) => (
    user?.assignedHotel?.id ||
    user?.assignedHotelId ||
    user?.hotel?.id ||
    user?.hotel ||
    user?.assigned_hotel ||
    ''
);

export default function UserFormModal({ open, onClose, onSave, editingUser = null, roles = [], hotels = [] }) {
    const defaultState = { 
        name: '', email: '', role: '', hotel: '', 
        status: 'ACTIVE', permissions: [], 
        phone: '', employeeId: '', department: '', joiningDate: '',
        passwordMode: 'auto', password: ''
    };
    const [formData, setFormData] = useState(defaultState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [linkingPromptOpen, setLinkingPromptOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [allPermissions, setAllPermissions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const { user, profile, tenants } = useSelector((state) => state.auth);
    const loggedInRole = normalizeRole(user?.role);
    const isSuperAdmin = loggedInRole === 'SUPERADMIN';
    const isAdmin = loggedInRole === 'ADMIN';
    
    // Check if a specific tenant is selected globally
    const hasActiveTenant = user?.tenantId && user.tenantId !== 'null';

    const shouldShowHotelDropdown = isSuperAdmin && !hasActiveTenant;

    // Determine the locked hotel ID and name
    let lockedHotelId = '';
    let lockedHotelName = '';

    if (isAdmin) {
        lockedHotelId = getHotelId(profile) || getHotelId(user);
        lockedHotelName = profile?.assignedHotel?.name || 
                          user?.assignedHotel?.name || 
                          hotels.find(h => h.id === lockedHotelId)?.name || 
                          '';
    } else if (isSuperAdmin && hasActiveTenant) {
        const activeHotel = hotels.find(h => String(h.tenantId) === String(user.tenantId) || String(h.id) === String(user.tenantId) || String(h._id) === String(user.tenantId));
        const activeTenant = (tenants || []).find(t => String(t.tenantId) === String(user.tenantId) || String(t.id || t._id) === String(user.tenantId));
        
        lockedHotelId = activeHotel ? (activeHotel.id || activeHotel._id) : (activeTenant ? (activeTenant.id || activeTenant._id || user.tenantId) : user.tenantId);
        lockedHotelName = activeHotel?.name || activeHotel?.hotelName || activeTenant?.name || activeTenant?.hotelName || `Tenant ID: ${user.tenantId}`;
    }

    // Populate data when modal opens and editingUser changes
    useEffect(() => {
        if (open) {
            if (editingUser) {
                setFormData({
                    ...defaultState,
                    ...editingUser,
                    role: editingUser.roleId || editingUser.role?.id || '',
                    permissions: editingUser.customPermissions || [],
                    assignedProperties: editingUser.propertyIds || (getHotelId(editingUser) ? [getHotelId(editingUser)] : []),
                    hotel: getHotelId(editingUser),
                    joiningDate: editingUser.joiningDate ? editingUser.joiningDate.split('T')[0] : '',
                    phone: editingUser.phone || '',
                    department: editingUser.department || '',
                    employeeId: editingUser.employeeId || '',
                });
            } else {
                setFormData({
                    ...defaultState,
                    assignedProperties: (!shouldShowHotelDropdown && lockedHotelId) ? [lockedHotelId] : [],
                    hotel: (!shouldShowHotelDropdown && lockedHotelId) ? lockedHotelId : ''
                });
            }
            setErrors({});
            fetchPermissions();
            fetchDepartments();
        }
    }, [open, editingUser, isAdmin, lockedHotelId, shouldShowHotelDropdown]);
    
    const fetchDepartments = async () => {
        try {
            const data = await StaffService.getDepartments();
            setDepartments(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch departments:', error);
        }
    };

    const fetchPermissions = async () => {
        try {
            const responseData = await PermissionService.getAll();
            let extractedPermissions = [];

            if (Array.isArray(responseData)) {
                extractedPermissions = responseData;
            } else if (typeof responseData === 'object' && responseData !== null) {
                if (responseData.availablePermissions) {
                    extractedPermissions = responseData.availablePermissions;
                } else {
                    const rolesArr = responseData.roles || [];
                    if (Array.isArray(rolesArr)) {
                        const uniquePerms = new Set();
                        rolesArr.forEach(role => {
                            if (Array.isArray(role.permissions)) {
                                role.permissions.forEach(p => uniquePerms.add(typeof p === 'string' ? p : p?.name));
                            }
                        });
                        extractedPermissions = Array.from(uniquePerms);
                    }
                }
            }

            const safePermissions = extractedPermissions.filter(p => typeof p === 'string' && p.trim() !== '');
            setAllPermissions(safePermissions);
        } catch (error) {
            console.error('Failed to fetch permissions:', error);
        }
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'phone') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        if (name === 'name') {
            value = value.replace(/[0-9]/g, '');
        }
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        else if (/[0-9]/.test(formData.name)) newErrors.name = 'Name cannot contain numbers';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email format';
        if (!formData.role) newErrors.role = 'Role is required';
        
        if (!editingUser && formData.passwordMode === 'manual') {
            if (!formData.password) {
                newErrors.password = 'Password is required for manual mode';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }
        }

        // Optional field validations (only validate if filled)
        if (formData.phone && formData.phone.length !== 10) {
            newErrors.phone = 'Phone number must be exactly 10 digits';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirmSave = async (createLinked) => {
        setLinkingPromptOpen(false);
        setIsSubmitting(true);
        try {
            await onSave({ ...formData, createLinkedStaff: createLinked });
            onClose();
        } catch (error) {
            console.error('Failed to save user:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            const selectedRoleObj = roles.find(r => r.id === formData.role || r._id === formData.role || r.name === formData.role);
            const rName = selectedRoleObj ? (typeof selectedRoleObj.name === 'object' ? selectedRoleObj.name.name : selectedRoleObj.name) : (typeof formData.role === 'string' ? formData.role : '');
            
            if (isStaffBasedRoleName(rName)) {
                const isAlreadyLinked = editingUser && (
                    editingUser.hasLinkedStaff === true || 
                    editingUser.isLinkedToStaff === true || 
                    editingUser.staffId || 
                    editingUser.staff || 
                    editingUser.linkedStaff || 
                    editingUser.staffProfileId
                );

                if (!isAlreadyLinked) {
                    setLinkingPromptOpen(true);
                    return;
                }
            }
            
            setIsSubmitting(true);
            try {
                await onSave(formData);
                onClose();
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleModalClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
    };

    return (
        <>
            <Modal open={open} onClose={handleModalClose} disableEscapeKeyDown>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 520 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
                display: 'flex', flexDirection: 'column', gap: 2,
                maxHeight: '90vh', overflowY: 'auto'
            }}>
                <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose}>
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold">
                    {editingUser ? 'Edit User' : 'Add New User'}
                </Typography>

                {/* ── Required Fields ──────────────────────────────── */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            size="small"
                            required
                            autoComplete="new-password"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            size="small"
                            disabled={!!editingUser}
                            required
                            autoComplete="new-password"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!errors.role} size="small" required>
                            <InputLabel>Role</InputLabel>
                            <Select name="role" value={formData.role || ''} label="Role" onChange={handleChange}>
                                {roles
                                    .filter(r => {
                                        const rName = typeof r.name === 'object' ? r.name.name : r.name;
                                        const normalizedR = normalizeRole(rName);
                                        if (isSuperAdmin) return normalizedR !== 'SUPERADMIN';
                                        if (isAdmin) return normalizedR !== 'SUPERADMIN' && normalizedR !== 'ADMIN';
                                        return true;
                                    })
                                    .map(r => {
                                        const rName = typeof r.name === 'object' ? r.name.name : r.name;
                                        return (
                                            <MenuItem key={r.id} value={r.id}>{rName}</MenuItem>
                                        );
                                    })
                                }
                            </Select>
                            {errors.role && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.role}</Typography>}
                        </FormControl>
                    </Grid>

                    {!editingUser && (
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select name="status" value={formData.status || 'ACTIVE'} label="Status" onChange={handleChange}>
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                                    <MenuItem value="SUSPENDED">Suspended</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>

                {/* ── Hotel Assignment ──────────────────────────────── */}
                {shouldShowHotelDropdown ? (
                    <Autocomplete
                        multiple
                        options={hotels}
                        getOptionLabel={(option) => option.name}
                        value={hotels.filter(h => (formData.assignedProperties || []).includes(h.id))}
                        onChange={(event, newValue) => {
                            setFormData(prev => ({ ...prev, assignedProperties: newValue.map(v => v.id), hotel: newValue[0]?.id || '' }));
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Assigned Properties" error={!!errors.hotel} helperText={errors.hotel} size="small" />
                        )}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip label={option.name} {...getTagProps({ index })} size="small" />
                            ))
                        }
                    />
                ) : (
                    <TextField
                        fullWidth
                        label="Assigned Hotel"
                        value={lockedHotelName}
                        disabled
                        variant="outlined"
                        size="small"
                        helperText={isAdmin ? "Administrators are locked to their assigned hotel" : "Locked to the currently active property"}
                    />
                )}

                {/* ── Password Settings (Only for New Users) ─────────── */}
                {!editingUser && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Password Mode</InputLabel>
                                <Select 
                                    name="passwordMode" 
                                    value={formData.passwordMode} 
                                    label="Password Mode" 
                                    onChange={handleChange}
                                >
                                    <MenuItem value="auto">Auto-generate</MenuItem>
                                    <MenuItem value="manual">Manual Set</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {formData.passwordMode === 'manual' && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    size="small"
                                    required
                                    autoComplete="new-password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}

                {/* ── Optional Details ──────────────────────────────── */}
                <Divider sx={{ my: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Optional Details</Typography>
                </Divider>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Employee ID"
                            name="employeeId"
                            value={formData.employeeId}
                            onChange={handleChange}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Department</InputLabel>
                            <Select name="department" value={formData.department} label="Department" onChange={handleChange}>
                                <MenuItem value=""><em>None</em></MenuItem>
                                {departments.map(dept => (
                                    <MenuItem key={dept.id || dept._id} value={dept.id || dept._id}>{dept.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Joining Date"
                            name="joiningDate"
                            value={formData.joiningDate}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                </Grid>

                {/* ── Custom Permissions ──────────────────────────────── */}
                <Autocomplete
                    multiple
                    options={allPermissions.filter(name => !(formData.permissions || []).includes(name))}
                    value={formData.permissions || []}
                    onChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, permissions: newValue }));
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Custom Permissions" placeholder="Search permissions..." size="small" />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip label={option} {...getTagProps({ index })} size="small" color="primary" variant="outlined" />
                        ))
                    }
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Button fullWidth variant="outlined" onClick={onClose} size="large" disabled={isSubmitting}>Cancel</Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleSubmit} 
                        size="large"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                    >
                        {editingUser ? 'Update' : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Modal>

        {/* Smart Staff Linking Prompt */}
        <Dialog 
            open={linkingPromptOpen} 
            onClose={() => setLinkingPromptOpen(false)}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1.5,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
                {editingUser ? 'Link Staff Profile?' : 'Create Linked Staff Profile?'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                    {editingUser
                        ? 'This user is assigned to a staff-based operational role and is not yet linked to a staff profile. Would you like to create and link a corresponding staff profile for this user?'
                        : 'The selected role is a staff-based operational role. Would you like to automatically create and link a corresponding staff profile for this user?'}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
                <Button 
                    onClick={() => handleConfirmSave(false)} 
                    variant="outlined" 
                    color="inherit"
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                    Skip for now
                </Button>
                <Button 
                    onClick={() => handleConfirmSave(true)} 
                    variant="contained" 
                    color="primary"
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                    autoFocus
                >
                    {editingUser ? 'Link Staff Profile' : 'Create & Link'}
                </Button>
            </DialogActions>
        </Dialog>
    </>
);
}
